import type { ReactNode } from "react";

interface BrowserFrameProps {
	children?: ReactNode;
	url?: string;
	className?: string;
	label?: string;
	tabTitle?: string;
	faviconColor?: string;
	decorative?: boolean;
	dark?: boolean;
}

export function BrowserFrame({
	children,
	url = "cohort-hub.upsquad.us",
	className = "",
	label,
	tabTitle,
	faviconColor,
	decorative = true,
	dark,
}: BrowserFrameProps) {
	const chromeClass = dark ? "border-neutral-800 bg-neutral-900" : "border-default-200/80 bg-content1";
	return (
		<div
			{...(decorative ? { role: "img", "aria-label": label ?? `Browser preview of ${url}` } : {})}
			className={`overflow-hidden rounded-2xl border border-default-200 bg-content1 shadow-lift ${className}`}
			data-mock="browser-frame"
		>
			<div className={`flex items-center gap-3 border-b px-3 py-2.5 ${chromeClass}`}>
				<div className="flex items-center gap-1.5" aria-hidden="true">
					<span className="size-2.5 rounded-full bg-red-400" />
					<span className="size-2.5 rounded-full bg-yellow-400" />
					<span className="size-2.5 rounded-full bg-green-400" />
				</div>
				{tabTitle ? (
					<div
						className={`hidden items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-medium sm:flex ${dark ? "bg-neutral-800 text-neutral-200" : "bg-white text-default-600"}`}
						aria-hidden="true"
					>
						<span className="size-2 rounded-full" style={{ background: faviconColor ?? "oklch(0.6204 0.195 253.83)" }} />
						<span className="max-w-32 truncate">{tabTitle}</span>
					</div>
				) : null}
				<div
					className={`flex min-w-0 flex-1 items-center justify-center rounded-md border px-3 py-1 text-xs ${
						dark ? "border-neutral-700 bg-neutral-800 text-neutral-300" : "border-default-200/60 bg-content2 text-default-500"
					}`}
				>
					<span className="truncate font-medium">{url}</span>
				</div>
				<div className="flex w-10 items-center justify-end gap-1" aria-hidden="true">
					<span className="size-1 rounded-full bg-default-300" />
					<span className="size-1 rounded-full bg-default-300" />
					<span className="size-1 rounded-full bg-default-300" />
				</div>
			</div>
			<div className="bg-white">{children}</div>
		</div>
	);
}
